import { PdpScoreStatus, PdpStatus } from '@vizit/api';
import {
  Pill,
  PillLevel,
  ScoreStickerV2,
  ScoreStickerV2Size,
  Text,
  TextColor,
  TextSize,
  Tooltip,
  TooltipTrigger,
} from '@vizit/atoms';
import { cx } from '@vizit/theme';
import { formatDate } from '@vizit/utils';
import React from 'react';
import { Button } from 'react-aria-components';
import type { ProductListingData } from './product-listing-row';

/**
 * Checks if a product was recently refreshed (within the last 7 days)
 */
function wasRecentlyRefreshed(lastRefreshed?: Date | null): lastRefreshed is Date {
  if (!lastRefreshed) return false;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return lastRefreshed > sevenDaysAgo;
}

function lastRefreshedAt(item: ProductListingData): Date | null {
  return item.lastRefreshed ?? item.lastScraped ?? item.createdAt ?? null;
}

export interface ProductListingRowScoreProps {
  item: ProductListingData;
}

const ProductListingRowScore = React.memo<ProductListingRowScoreProps>(({ item }) => {
  const lastRefreshedDate = lastRefreshedAt(item);
  const isPreLaunch = item.sourceType === 'manual';
  const isRefreshEnabled = !isPreLaunch;
  const isRefreshing = isRefreshEnabled && item.status === PdpStatus.ScrapeStarted;

  // Pre-launch (staged) products aren't live on a retailer, so there's no live
  // score to report at any score status — show the same dash the Change column
  // uses for "no data" rather than a score, a Processing pill, or N/A.
  if (isPreLaunch) {
    return (
      <Text size={TextSize.Sm} color={TextColor.Tertiary} className="w-16 text-center">
        -
      </Text>
    );
  }

  // Shown beside the score (or the withheld marker) so a refresh reads as working either way.
  const refreshIndicator = isRefreshEnabled && wasRecentlyRefreshed(lastRefreshedDate) && (
    <TooltipTrigger delay={500}>
      <Button
        aria-label={isRefreshing ? 'Refreshing' : 'Recently refreshed'}
        className={cx(
          'flex items-center outline-none w-12 h-12 justify-center',
          isRefreshing && 'motion-safe:animate-pulse',
        )}
      >
        <div className="h-3 w-3 rounded-full bg-brand-600" aria-hidden="true" />
      </Button>
      <Tooltip>{isRefreshing ? 'Refreshing...' : `Last refresh: ${formatDate(lastRefreshedDate)}`}</Tooltip>
    </TooltipTrigger>
  );

  // A withheld formula score has no number to show; it must never read as a zero or an N/A. It
  // still pulses and carries the refresh indicator, since a refresh may bring a score back.
  if (item.isScoreWithheld) {
    return (
      <div className="flex items-center justify-start">
        <Pill level={PillLevel.Default} className={isRefreshing ? 'motion-safe:animate-pulse' : undefined}>
          Unavailable
        </Pill>
        {refreshIndicator}
      </div>
    );
  }

  switch (item.scoreStatus) {
    case PdpScoreStatus.Created:
      return <Pill level={PillLevel.Default}>Processing</Pill>;
    case PdpScoreStatus.Processing:
      return <Pill level={PillLevel.Default}>Processing</Pill>;
    case PdpScoreStatus.CategoryNotFound:
      return <Pill level={PillLevel.Error}>Error</Pill>;
    case PdpScoreStatus.RescorePending:
      return <Pill level={PillLevel.Default}>Processing</Pill>;
    case PdpScoreStatus.RefreshPending:
    case PdpScoreStatus.Scored:
      return (
        <div className="flex items-center justify-start">
          <ScoreStickerV2
            score={item.score ?? item.yourScore ?? null}
            size={ScoreStickerV2Size.Xs}
            isProcessing={isRefreshing}
            className={isRefreshing ? 'motion-safe:animate-pulse' : undefined}
          />
          {refreshIndicator}
        </div>
      );

    default:
      return <Pill level={PillLevel.Default}>N/A</Pill>;
  }
});

ProductListingRowScore.displayName = 'ProductListingRowScore';

export { ProductListingRowScore };
